import React, { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import Clear from "@mui/icons-material/Clear";
import LogoNegativo from "assets/images/quotas-logo-negativo.png";
import { apiPost } from "lib/api";
import { useGlobalMDAlert } from "context";
import {
  useMaterialUIController,
  setNombrenegocio,
  setAccesoweb,
  setRoleweb,
  setRoleDetalles,
  setModulos,
  setUsuarioLog,
  setClaveLog,
  setAnchoCodigoBarra,
  setAltoCodigoBarra,
  setComprobanteelectronico,
  setDireccionEmpresa,
  setRncEmpresa,
  setEnviarMotorContable,
} from "context";
import { tipoDispositivo } from "lib/dispositivo";

// Línea gráfica Quotas (quotas-landing)
const NAVY = "#011763";
const NAVY_DEEP = "#010f45";
const BLUE = "#0040ff";
const BLUE_DARK = "#003bcd";
const CYAN = "#5ce1ff";
const INK = "#0d1533";
const MUTED = "#5b638a";
const FAINT = "#8990b0";
const LINE = "#edeff7";
const BG = "#fbfcfe";
const SORA = "'Sora', sans-serif";
const MANROPE = "'Manrope', system-ui, sans-serif";

export default function Login() {
  const [usuario, setUsuario] = useState("");
  const [clave, setClave] = useState("");
  const [verPass, setVerPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [, dispatch] = useMaterialUIController();
  const navigate = useNavigate();
  const refUsuario = useRef(null);
  const { showAlert, setShowAlert } = useGlobalMDAlert();

  function handleLogin() {
    setError("");
    if (!String(usuario).trim()) {
      refUsuario.current?.focus();
      return;
    }
    if (!String(clave).trim()) {
      setError("Ingrese su contraseña.");
      return;
    }
    if (loading) return;

    setLoading(true);
    apiPost("/api/loginbackend_buscardatosiniciales", {
      usuario,
      password: clave,
      dispositivo: tipoDispositivo(),
    })
      .then(({ status, data }) => {
        if (status === 200) {
          setClaveLog(dispatch, clave);
          setUsuarioLog(dispatch, usuario);
          setNombrenegocio(dispatch, data.nombrenegocio[0].ncomp);
          setAnchoCodigoBarra(dispatch, Number(data.nombrenegocio[0].anchocodigobarra) || 0);
          setAltoCodigoBarra(dispatch, Number(data.nombrenegocio[0].altocodigobarra) || 0);
          setAccesoweb(dispatch, data.usuarios[0].web);
          setRoleweb(dispatch, data.usuarios[0].tipo);
          setRoleDetalles(dispatch, data.roles);
          setModulos(dispatch, data.modulos);
          setDireccionEmpresa(dispatch, data.direccion || data.nombrenegocio[0].direccion || "");
          setRncEmpresa(dispatch, data.rnc || data.nombrenegocio[0].rnc || "");
          setEnviarMotorContable(dispatch, data.enviarMotorContable || data.nombrenegocio[0].enviarMotorContable || "N");
          setComprobanteelectronico(
            dispatch,
            data.comprobanteelectronico || data.nombrenegocio[0].comprobanteelectronico || "N"
          );
          localStorage.setItem("id", data.id);
          localStorage.setItem("usuario", "");
          localStorage.setItem("password", "");
          localStorage.setItem("roleweb", "");
          localStorage.setItem("sucursal", data.usuarios[0].sucursal);
          // Nombre de la sucursal del usuario (para el encabezado del menú).
          const sucUsuario = String(data.usuarios[0].sucursal ?? "").trim();
          const sucursalInfo = (data.sucursales || []).find(
            (s) => String(s.sucursal).trim() === sucUsuario
          );
          localStorage.setItem("sucursalnombre", String(sucursalInfo?.descripcion || "").trim());
          localStorage.setItem("nombrenegocio", data.nombrenegocio[0].ncomp);
          localStorage.setItem("sucursalmatricula", data.nombrenegocio[0].sucursalmatricula);
          localStorage.setItem("anchocodigobarra", Number(data.nombrenegocio[0].anchocodigobarra) || 0);
          localStorage.setItem("altocodigobarra", Number(data.nombrenegocio[0].altocodigobarra) || 0);
          // Token de sesión: permite restaurar la sesión tras un refresh
          // (ver SesionPersistente). Si el backend aún no lo emite, no pasa nada.
          if (data.session_token) localStorage.setItem("session_token", data.session_token);
          const web = data.usuarios[0].web;
          if (web === "S" || web === "s") {
            navigate("/moduloscliente");
          } else {
            showAlert;
          }
        }
        if (status === 400) {
          showAlert({ message: `Error: ${data.Error}`, color: "error", autoHideDuration: 3000 });
        }
      })
      .catch((err) => showAlert({ message: err?.message || "Error de conexión", color: "error", autoHideDuration: 3000 }))
      .finally(() => setLoading(false));
  }

  const inputSx = {
    mb: 2.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: "10px",
      bgcolor: "#FFFFFF",
      fontFamily: MANROPE,
      fontSize: { xs: "1.0625rem", md: "1.125rem" },
      py: 0.25,
      "& fieldset": { borderColor: LINE, borderWidth: "1.5px" },
      "&:hover fieldset": { borderColor: "#d5daf0" },
      "&.Mui-focused fieldset": { borderColor: BLUE },
    },
    "& .MuiInputLabel-root": {
      color: MUTED,
      fontFamily: MANROPE,
      fontWeight: 600,
      fontSize: { xs: "0.9375rem", md: "1rem" },
      "&.Mui-focused": { color: BLUE },
    },
  };

  return (
    <Box
      sx={{
        minHeight: { xs: "100dvh", md: "100vh" },
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        bgcolor: BG,
        overflowX: "hidden",
        pt: { xs: "env(safe-area-inset-top)", md: 0 },
        pb: { xs: "env(safe-area-inset-bottom)", md: 0 },
        pl: { xs: "env(safe-area-inset-left)", md: 0 },
        pr: { xs: "env(safe-area-inset-right)", md: 0 },
      }}
    >
      {/* Panel institucional — hero Quotas */}
      <Box
        sx={{
          position: "relative",
          background: `radial-gradient(120% 130% at 78% 8%, ${BLUE} 0%, ${BLUE_DARK} 34%, ${NAVY} 72%, ${NAVY_DEEP} 100%)`,
          overflow: "hidden",
          px: { xs: 3, sm: 4, md: 7 },
          py: { xs: 3.5, sm: 4, md: 10 },
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          width: { md: "44%", lg: "40%" },
          flexShrink: 0,
          minHeight: { xs: "auto", md: "min(100dvh, 100vh)" },
        }}
      >
        {/* Cuadrícula sutil del hero */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            backgroundImage:
              "linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px)",
            backgroundSize: "46px 46px",
            opacity: 0.5,
            pointerEvents: "none",
          }}
        />
        <Box sx={{ position: "relative" }}>
          <Box
            component="img"
            src={LogoNegativo}
            alt="Quotas"
            sx={{ height: { xs: 60, md: 76 }, width: "auto", mb: { xs: 2.5, md: 4 }, ml: { xs: -1, md: -1.5 } }}
          />
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              bgcolor: "rgba(255,255,255,.1)",
              border: "1px solid rgba(255,255,255,.2)",
              color: "#fff",
              fontFamily: MANROPE,
              fontSize: "12.5px",
              fontWeight: 700,
              px: "15px",
              py: "7px",
              borderRadius: "100px",
              mb: { xs: 2, md: 3.25 },
              backdropFilter: "blur(6px)",
            }}
          >
            <Box sx={{ width: 7, height: 7, bgcolor: CYAN, borderRadius: "2px" }} />
            Portal de gestión empresarial
          </Box>
          <Typography
            sx={{
              fontFamily: SORA,
              fontWeight: 800,
              fontSize: { xs: "1.5rem", sm: "1.7rem", md: "2rem" },
              letterSpacing: "-0.03em",
              lineHeight: 1.1,
              mb: 1.75,
              maxWidth: { xs: "100%", sm: 400 },
              color: "#fff",
            }}
          >
            Importa, vende y financia sin cambiar de sistema.
          </Typography>
          <Typography
            sx={{
              fontFamily: MANROPE,
              color: "rgba(255,255,255,.8)",
              fontSize: { xs: "0.9375rem", md: "1rem" },
              lineHeight: 1.6,
              maxWidth: { xs: "100%", sm: 360 },
            }}
          >
            Acceso autorizado para personal registrado. Las credenciales son personales e intransferibles.
          </Typography>
        </Box>

        {/* Mockup decorativo de la app — mismo hero-browser de la landing */}
        <Box sx={{ position: "relative", display: { xs: "none", md: "block" }, my: 4 }}>
          <Box
            sx={{
              bgcolor: "#fff",
              borderRadius: "16px",
              border: "1px solid rgba(255,255,255,.4)",
              boxShadow: "0 18px 60px rgba(0,25,110,.5)",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                px: 2,
                py: 1.4,
                borderBottom: "1px solid #f0f1f8",
                bgcolor: "#fafbff",
              }}
            >
              {["a", "b", "c"].map((k) => (
                <Box key={k} sx={{ width: 9, height: 9, borderRadius: "50%", bgcolor: "#e2e5f0" }} />
              ))}
            </Box>
            <Box sx={{ display: "grid", gridTemplateColumns: { md: "84px 1fr", lg: "110px 1fr" }, minHeight: 180 }}>
              <Box
                sx={{
                  bgcolor: "#f6f8fe",
                  borderRight: "1px solid #eef0f8",
                  p: "16px 14px",
                  display: "flex",
                  flexDirection: "column",
                  gap: "11px",
                }}
              >
                {["70%", "55%", "65%", "48%", "60%"].map((w, i) => (
                  <Box key={w} sx={{ height: 9, width: w, borderRadius: "3px", bgcolor: i === 0 ? BLUE : "#dfe4f6" }} />
                ))}
              </Box>
              <Box sx={{ p: "18px" }}>
                <Box sx={{ display: "flex", gap: "10px", mb: "14px" }}>
                  {[
                    { label: "Motos en stock", valor: "1,284" },
                    { label: "Créditos activos", valor: "3,910" },
                    { label: "Cobrado hoy", valor: "$182K", hl: true },
                  ].map((kpi) => (
                    <Box
                      key={kpi.label}
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        bgcolor: kpi.hl ? "#eef4ff" : "#f4f6fc",
                        borderRadius: "9px",
                        p: { md: "10px", lg: "12px" },
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: MANROPE,
                          fontSize: "10px",
                          fontWeight: 600,
                          lineHeight: 1.25,
                          color: kpi.hl ? BLUE_DARK : FAINT,
                        }}
                      >
                        {kpi.label}
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: SORA,
                          fontWeight: 700,
                          fontSize: { md: "16px", lg: "19px" },
                          color: kpi.hl ? BLUE : NAVY,
                        }}
                      >
                        {kpi.valor}
                      </Typography>
                    </Box>
                  ))}
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-end", gap: "7px", height: 78 }}>
                  {[
                    { h: "50%", c: "#dfe4f6" },
                    { h: "66%", c: "#dfe4f6" },
                    { h: "58%", c: "#c3ccf0" },
                    { h: "82%", c: BLUE_DARK },
                    { h: "100%", c: BLUE },
                    { h: "70%", c: "#c3ccf0" },
                    { h: "54%", c: "#dfe4f6" },
                  ].map((b) => (
                    <Box key={b.h + b.c} sx={{ flex: 1, height: b.h, bgcolor: b.c, borderRadius: "4px 4px 0 0" }} />
                  ))}
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>

        <Typography
          sx={{
            position: "relative",
            display: { xs: "none", md: "block" },
            color: "rgba(255,255,255,.55)",
            fontFamily: MANROPE,
            fontSize: "0.75rem",
            mt: 4,
          }}
        >
          © {new Date().getFullYear()} Quotas · Uso interno
        </Typography>
      </Box>

      {/* Formulario */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: { xs: 2.5, sm: 4, md: 7 },
          width: "100%",
          minWidth: 0,
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: { xs: "100%", sm: 600, md: 680, lg: 720 },
            bgcolor: "#FFFFFF",
            border: `1px solid ${LINE}`,
            borderRadius: "14px",
            p: { xs: 4, sm: 5.5, md: 7 },
            boxShadow: "0 4px 24px rgba(1, 23, 99, 0.06)",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontFamily: SORA,
              fontWeight: 700,
              color: NAVY,
              letterSpacing: "-0.02em",
              mb: 1,
              fontSize: { xs: "1.45rem", sm: "1.55rem", md: "1.7rem" },
            }}
          >
            Inicio de sesión
          </Typography>
          <Typography
            variant="body1"
            sx={{
              fontFamily: MANROPE,
              color: MUTED,
              mb: 3.5,
              lineHeight: 1.6,
              fontSize: { xs: "1rem", md: "1.0625rem" },
              fontWeight: 600,
            }}
          >
            Introduzca usuario y contraseña.
          </Typography>

          <TextField
            inputRef={refUsuario}
            label="Usuario"
            fullWidth
            value={usuario}
            onChange={(e) => setUsuario(e.target.value)}
            variant="outlined"
            disabled={loading}
            autoComplete="username"
            InputLabelProps={{ shrink: true }}
            sx={inputSx}
            InputProps={{
              endAdornment:
                String(usuario).length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      aria-label="Limpiar usuario"
                      onClick={() => {
                        setUsuario("");
                        setError("");
                        refUsuario.current?.focus();
                      }}
                      sx={{ color: FAINT }}
                    >
                      <Clear sx={{ fontSize: 20 }} />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
            }}
          />

          <TextField
            label="Contraseña"
            fullWidth
            type={verPass ? "text" : "password"}
            value={clave}
            onChange={(e) => setClave(e.target.value)}
            variant="outlined"
            disabled={loading}
            autoComplete="current-password"
            InputLabelProps={{ shrink: true }}
            sx={{ ...inputSx, mb: error ? 1 : 2 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleLogin();
            }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    onClick={() => setVerPass(!verPass)}
                    aria-label={verPass ? "Ocultar contraseña" : "Mostrar contraseña"}
                    sx={{ color: FAINT }}
                  >
                    {verPass ? <VisibilityOff sx={{ fontSize: 22 }} /> : <Visibility sx={{ fontSize: 22 }} />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          {error ? (
            <Typography sx={{ color: "#B42318", fontFamily: MANROPE, fontSize: "0.8125rem", mb: 2 }}>
              {error}
            </Typography>
          ) : null}

          <Button
            fullWidth
            variant="contained"
            disableElevation
            disabled={loading}
            onClick={handleLogin}
            sx={{
              py: { xs: 1.5, md: 1.65 },
              borderRadius: "12px",
              textTransform: "none",
              fontFamily: MANROPE,
              fontWeight: 700,
              fontSize: { xs: "1.0625rem", md: "1.125rem" },
              color: "#FFFFFF",
              bgcolor: BLUE,
              transition: "filter .15s ease, transform .15s ease",
              "&:hover": { bgcolor: BLUE, color: "#FFFFFF", filter: "brightness(1.08)" },
              "&:active": { transform: "translateY(1px)" },
              "&.Mui-disabled": { color: "#FFFFFF", bgcolor: BLUE, opacity: 0.85 },
            }}
          >
            {loading ? (
              <CircularProgress size={26} thickness={4} sx={{ color: "#FFFFFF" }} aria-label="Iniciando sesión" />
            ) : (
              "Continuar"
            )}
          </Button>

          <Typography
            variant="body2"
            sx={{
              display: "block",
              textAlign: "center",
              mt: 3,
              color: MUTED,
              fontFamily: MANROPE,
              fontSize: "0.875rem",
            }}
          >
            Si olvidó sus datos, contacte al administrador del sistema.
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
